import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { SearchInput } from '../components/search/SearchInput';
import { SearchSuggestions } from '../components/search/SearchSuggestions';
import { SearchResults } from '../components/search/SearchResults';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';

const Search: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [lastSearchedTerm, setLastSearchedTerm] = useState<string>('');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { t } = useI18n();

  // Função para salvar termo de busca
  const saveSearchTerm = async (term: string, userId: string) => {
    try {
      const token = localStorage.getItem('supabase.auth.token');
      const parsedToken = token ? JSON.parse(token).access_token : null;
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/save_search_term`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${parsedToken}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || ''
        },
        body: JSON.stringify({ term, userId })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('Save search term error:', error);
      throw error;
    }
  };

  // Restaurar estado da busca quando retornar da página de detalhe
  useEffect(() => {
    if (location.state?.searchState && location.state?.preserveSearch) {
      setSearchQuery(location.state.searchState.query || '');
      setIsSearching(location.state.searchState.query ? true : false);
      setLastSearchedTerm(location.state.searchState.query || '');
    }
  }, [location.state]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setIsSearching(query.trim().length > 0);
  };

  const handleSearchSubmit = async (query: string) => {
    setSearchQuery(query);
    setIsSearching(true);
    
    // Salvar o termo de busca apenas quando o usuário pressionar Enter
    if (user?.id && query.trim()) {
      try {
        await saveSearchTerm(query.trim(), user.id);
        
        // Atualizar o último termo buscado para atualizar a lista localmente
        setLastSearchedTerm(query.trim());
      } catch (error) {
        console.error('❌ Failed to save search term on Enter:', error);
      }
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    setLastSearchedTerm(''); // Limpar o último termo buscado
  };

  // Teste: se não há usuário logado, usar um ID fixo para teste
  const testUserId = user?.id || 'b85f7965-8e9f-449b-9718-08a5b2fa0f3b'; // ID da Ana para teste

  return (
    <div className="min-h-screen w-screen pb-20" style={{ background: '#160C0C' }}>
      <TopNavigation variant="subtle" />
      
      <main className="pt-16 px-5">
        <div className="py-6">
          <SearchInput 
            value={searchQuery}
            onChange={handleSearch}
            onClear={handleClearSearch}
            onSearch={handleSearchSubmit}
            placeholder={t('search.find_users_and_inspiration')}
          />
          
          {!isSearching ? (
            <SearchSuggestions onSearch={handleSearch} newSearchTerm={lastSearchedTerm} />
          ) : (
            <SearchResults 
              query={searchQuery} 
              userId={testUserId}
            />
          )}
        </div>
      </main>

      <BottomNavigation />
    </div>
  );
};

export default Search; 